import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
    User,
    Share2,
    MessageSquareQuote,
    Check,
    ArrowRight,
    ArrowLeft,
    Instagram,
    Facebook,
    Youtube,
    Linkedin,
    Plus,
    Trash2,
} from "lucide-react";

const STEPS = [
    { key: "personal", label: "Personal info", icon: User },
    { key: "social", label: "Social presence", icon: Share2 },
    { key: "motivation", label: "Motivation", icon: MessageSquareQuote },
];

const SOCIALS = [
    { key: "instagram", label: "Instagram", icon: Instagram, placeholder: "https://instagram.com/handle" },
    { key: "facebook", label: "Facebook", icon: Facebook, placeholder: "https://facebook.com/page" },
    { key: "tiktok", label: "TikTok", icon: Share2, placeholder: "https://tiktok.com/@handle" },
    { key: "linkedin", label: "LinkedIn", icon: Linkedin, placeholder: "https://linkedin.com/in/name" },
    { key: "youtube", label: "YouTube", icon: Youtube, placeholder: "https://youtube.com/@channel" },
];

const NICHES = [
    "Lifestyle", "Family & parenting", "Finance", "Fitness & wellness",
    "Travel", "Food", "Beauty", "Tech", "Business", "Other",
];

export default function AffiliateApply() {
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [busy, setBusy] = useState(false);

    // Personal
    const [fullName, setFullName] = useState("");
    const [email, setEmail] = useState("");
    const [businessName, setBusinessName] = useState("");
    const [website, setWebsite] = useState("");

    // Social
    const [socials, setSocials] = useState({});
    const [contentLinks, setContentLinks] = useState([""]);

    // Motivation
    const [whyPromote, setWhyPromote] = useState("");
    const [audience, setAudience] = useState("");
    const [niche, setNiche] = useState("");
    const [priorExp, setPriorExp] = useState("");
    const [termsAccepted, setTermsAccepted] = useState(false);

    // Validation per step
    const canGoNext = (() => {
        if (step === 0) {
            return fullName.trim().length >= 2 && /.+@.+\..+/.test(email);
        }
        if (step === 1) {
            // Content links are OPTIONAL — some creators lean on their
            // profiles. Require at least one social so we have something
            // to review though.
            const anySocial = Object.values(socials).some(
                (v) => typeof v === "string" && v.trim().length > 4
            );
            return anySocial;
        }
        if (step === 2) {
            return (
                whyPromote.trim().length >= 20 &&
                audience.trim().length >= 20 &&
                termsAccepted
            );
        }
        return false;
    })();

    const submit = async () => {
        setBusy(true);
        try {
            const payload = {
                full_name: fullName.trim(),
                email: email.trim().toLowerCase(),
                business_name: businessName.trim() || null,
                website: website.trim() || null,
                socials: Object.fromEntries(
                    Object.entries(socials).filter(
                        ([, v]) => typeof v === "string" && v.trim()
                    )
                ),
                content_links: contentLinks
                    .map((l) => l.trim())
                    .filter(Boolean),
                why_promote: whyPromote.trim(),
                audience_description: audience.trim(),
                niche: niche || null,
                prior_experience: priorExp.trim() || null,
                terms_accepted: termsAccepted,
            };
            const { data } = await api.post("/affiliate-applications", payload);
            navigate(`/affiliates/apply/success?id=${data.id}`, { replace: true });
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const StepIcon = STEPS[step].icon;

    return (
        <div className="min-h-screen bg-cream" data-testid="affiliate-apply-page">
            <header className="px-6 lg:px-12 py-6 flex items-center justify-between">
                <Link to="/" className="font-display text-2xl text-navy-700">
                    Perx <span className="text-gold-600">Group</span>
                </Link>
                <Link
                    to="/login"
                    className="text-sm text-navy-500 hover:text-navy-700 underline underline-offset-4"
                >
                    Already an affiliate? Sign in
                </Link>
            </header>

            <main className="max-w-3xl mx-auto px-6 lg:px-12 pb-20">
                <div className="text-center mb-10">
                    <div className="inline-flex items-center gap-1.5 rounded-full bg-navy-700 text-cream px-3 py-1 text-[11px] font-semibold tracking-widest uppercase">
                        Become a Perx Affiliate
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl text-navy-700 mt-4 leading-tight">
                        Promote what you already love.
                    </h1>
                    <p className="text-navy-500 mt-3 max-w-xl mx-auto">
                        We review every affiliate application by hand to make sure your
                        online presence and content style align with the Perx brand.
                        Expect a decision in 3-5 business days.
                    </p>
                    <div className="mt-6 grid sm:grid-cols-3 gap-3 text-left max-w-2xl mx-auto">
                        <BrandValueTile
                            title="Warm, curated"
                            body="Members-first tone. No spammy discounting."
                        />
                        <BrandValueTile
                            title="Trustworthy"
                            body="Vet everything you promote — vendors and offers alike."
                        />
                        <BrandValueTile
                            title="Long-term"
                            body="We reward consistency over one-off campaigns."
                        />
                    </div>
                </div>

                {/* Progress */}
                <div
                    className="rounded-2xl border border-navy-100 bg-white p-6 mb-6"
                    data-testid="affiliate-apply-progress"
                >
                    <div className="flex items-center gap-3">
                        {STEPS.map((s, i) => (
                            <div key={s.key} className="flex-1 flex items-center gap-2">
                                <div
                                    className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold ${
                                        i < step
                                            ? "bg-emerald-500 text-white"
                                            : i === step
                                            ? "bg-navy-700 text-cream"
                                            : "bg-navy-50 text-navy-400"
                                    }`}
                                >
                                    {i < step ? <Check size={14} /> : i + 1}
                                </div>
                                <span
                                    className={`text-xs font-semibold uppercase tracking-widest ${
                                        i === step ? "text-navy-700" : "text-navy-400"
                                    }`}
                                >
                                    {s.label}
                                </span>
                                {i < STEPS.length - 1 && (
                                    <div className="flex-1 h-px bg-navy-100 mx-2" />
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Card */}
                <div className="rounded-3xl border border-navy-100 bg-white p-6 sm:p-8">
                    <div className="flex items-center gap-2 mb-6">
                        <StepIcon size={20} className="text-gold-600" />
                        <h2 className="font-display text-2xl text-navy-700">
                            {STEPS[step].label}
                        </h2>
                    </div>

                    {step === 0 && (
                        <PersonalStep
                            {...{ fullName, setFullName, email, setEmail, businessName, setBusinessName, website, setWebsite }}
                        />
                    )}
                    {step === 1 && (
                        <SocialStep
                            socials={socials}
                            setSocials={setSocials}
                            contentLinks={contentLinks}
                            setContentLinks={setContentLinks}
                        />
                    )}
                    {step === 2 && (
                        <MotivationStep
                            {...{
                                whyPromote,
                                setWhyPromote,
                                audience,
                                setAudience,
                                niche,
                                setNiche,
                                priorExp,
                                setPriorExp,
                                termsAccepted,
                                setTermsAccepted,
                            }}
                        />
                    )}

                    <div className="flex justify-between gap-3 pt-6 mt-6 border-t border-navy-100">
                        <Button
                            variant="outline"
                            disabled={step === 0}
                            onClick={() => setStep((s) => Math.max(0, s - 1))}
                            data-testid="apply-back-btn"
                            className="rounded-full h-11 border-navy-200 text-navy-700 disabled:opacity-30"
                        >
                            <ArrowLeft size={14} className="mr-2" />
                            Back
                        </Button>
                        {step < STEPS.length - 1 ? (
                            <Button
                                disabled={!canGoNext}
                                onClick={() => setStep((s) => s + 1)}
                                data-testid="apply-next-btn"
                                className="rounded-full h-11 bg-navy-700 hover:bg-navy-800 text-cream disabled:opacity-50 px-8"
                            >
                                Continue
                                <ArrowRight size={14} className="ml-2" />
                            </Button>
                        ) : (
                            <Button
                                disabled={!canGoNext || busy}
                                onClick={submit}
                                data-testid="apply-submit-btn"
                                className="rounded-full h-11 bg-gold-500 hover:bg-gold-600 text-navy-800 disabled:opacity-50 px-8 font-semibold"
                            >
                                {busy ? "Submitting…" : "Submit application"}
                            </Button>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}

function PersonalStep({ fullName, setFullName, email, setEmail, businessName, setBusinessName, website, setWebsite }) {
    return (
        <div className="space-y-4">
            <div>
                <Label>Full name *</Label>
                <Input
                    required
                    data-testid="apply-name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="mt-1.5 h-11"
                />
            </div>
            <div>
                <Label>Email *</Label>
                <Input
                    type="email"
                    required
                    data-testid="apply-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1.5 h-11"
                />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
                <div>
                    <Label>Business name (optional)</Label>
                    <Input
                        data-testid="apply-business"
                        value={businessName}
                        onChange={(e) => setBusinessName(e.target.value)}
                        className="mt-1.5 h-11"
                    />
                </div>
                <div>
                    <Label>Website / blog (optional)</Label>
                    <Input
                        data-testid="apply-website"
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                        placeholder="https://"
                        className="mt-1.5 h-11"
                    />
                </div>
            </div>
        </div>
    );
}

function SocialStep({ socials, setSocials, contentLinks, setContentLinks }) {
    const updateSocial = (k, v) => setSocials((s) => ({ ...s, [k]: v }));
    const updateContent = (i, v) =>
        setContentLinks((l) => l.map((x, idx) => (idx === i ? v : x)));
    return (
        <div className="space-y-5">
            <p className="text-sm text-navy-500">
                Share at least one active profile — the more we can see, the faster we
                can review your application.
            </p>
            <div className="grid gap-3">
                {SOCIALS.map((s) => {
                    const Icon = s.icon;
                    return (
                        <div key={s.key} className="flex gap-2 items-center">
                            <div className="w-28 flex items-center gap-2 text-sm text-navy-500">
                                <Icon size={14} />
                                {s.label}
                            </div>
                            <Input
                                data-testid={`apply-social-${s.key}`}
                                value={socials[s.key] || ""}
                                onChange={(e) => updateSocial(s.key, e.target.value)}
                                placeholder={s.placeholder}
                                className="h-11 flex-1"
                            />
                        </div>
                    );
                })}
            </div>

            <div>
                <Label>
                    Recent content you&apos;re proud of{" "}
                    <span className="text-navy-400 font-normal">(optional)</span>
                </Label>
                <p className="text-[11px] text-navy-400 mt-0.5">
                    Share 1-5 links to standout pieces of yours, or leave blank
                    and we&rsquo;ll review directly from your linked profiles.
                </p>
                <div className="space-y-2 mt-2">
                    {contentLinks.map((link, i) => (
                        <div key={i} className="flex gap-2">
                            <Input
                                data-testid={`apply-content-${i}`}
                                value={link}
                                onChange={(e) => updateContent(i, e.target.value)}
                                placeholder="https://"
                                className="h-11"
                            />
                            {contentLinks.length > 1 && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() =>
                                        setContentLinks((l) => l.filter((_, idx) => idx !== i))
                                    }
                                    data-testid={`apply-content-remove-${i}`}
                                    className="h-11 w-11 text-navy-400"
                                >
                                    <Trash2 size={14} />
                                </Button>
                            )}
                        </div>
                    ))}
                    {contentLinks.length < 5 && (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setContentLinks((l) => [...l, ""])}
                            data-testid="apply-content-add"
                            className="rounded-full text-xs h-8 border-navy-200 text-navy-700"
                        >
                            <Plus size={12} className="mr-1" />
                            Add another link
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}

function MotivationStep({
    whyPromote,
    setWhyPromote,
    audience,
    setAudience,
    niche,
    setNiche,
    priorExp,
    setPriorExp,
    termsAccepted,
    setTermsAccepted,
}) {
    return (
        <div className="space-y-5">
            <div>
                <Label>Why do you want to promote Perx? *</Label>
                <Textarea
                    data-testid="apply-why"
                    value={whyPromote}
                    onChange={(e) => setWhyPromote(e.target.value)}
                    rows={4}
                    placeholder="Tell us what Perx means to you and why it fits your audience. Minimum 20 characters."
                    className="mt-1.5 resize-none"
                />
                <div className="text-[11px] text-navy-400 mt-1">
                    {whyPromote.trim().length} characters
                </div>
            </div>
            <div>
                <Label>Describe your audience *</Label>
                <Textarea
                    data-testid="apply-audience"
                    value={audience}
                    onChange={(e) => setAudience(e.target.value)}
                    rows={4}
                    placeholder="Size, demographics, engagement style. Minimum 20 characters."
                    className="mt-1.5 resize-none"
                />
                <div className="text-[11px] text-navy-400 mt-1">
                    {audience.trim().length} characters
                </div>
            </div>
            <div>
                <Label>Content category / niche</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                    {NICHES.map((n) => (
                        <button
                            type="button"
                            key={n}
                            onClick={() => setNiche((v) => (v === n ? "" : n))}
                            data-testid={`apply-niche-${n.toLowerCase().replace(/\s+/g, "-")}`}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                                niche === n
                                    ? "bg-navy-700 text-cream"
                                    : "bg-cream border border-navy-100 text-navy-700 hover:border-navy-300"
                            }`}
                        >
                            {n}
                        </button>
                    ))}
                </div>
            </div>
            <div>
                <Label>Prior affiliate experience (optional)</Label>
                <Textarea
                    data-testid="apply-prior"
                    value={priorExp}
                    onChange={(e) => setPriorExp(e.target.value)}
                    rows={3}
                    placeholder="Brands you've worked with, results, whatever's relevant."
                    className="mt-1.5 resize-none"
                />
            </div>

            <label
                htmlFor="apply-terms"
                className="flex items-start gap-3 rounded-xl bg-cream border border-gold-200/60 p-4 cursor-pointer"
            >
                <Checkbox
                    id="apply-terms"
                    data-testid="apply-terms"
                    checked={termsAccepted}
                    onCheckedChange={(v) => setTermsAccepted(Boolean(v))}
                />
                <span className="text-sm text-navy-700 leading-relaxed">
                    I agree to the{" "}
                    <a
                        href="/faq"
                        target="_blank"
                        rel="noreferrer"
                        className="underline text-navy-700"
                    >
                        Perx affiliate terms &amp; conditions
                    </a>
                    , including the requirement to disclose the affiliate relationship
                    on every piece of promotional content.
                </span>
            </label>
        </div>
    );
}

function BrandValueTile({ title, body }) {
    return (
        <div className="rounded-xl bg-white border border-navy-100 p-4">
            <div className="text-navy-700 font-semibold text-sm">{title}</div>
            <div className="text-[11px] text-navy-500 mt-1">{body}</div>
        </div>
    );
}
